import { UsClientError, type UsClientSession } from './tradingClient';
import type { UsScope } from './types';

export interface UsVisibleAccount {
  accountId: string; accountEnabled: boolean; ownership: 'personal' | 'pooled';
  canViewOwn: boolean; canViewDesk: boolean; execution: 'not_evaluated' | 'not_activated';
}
export interface UsAccountContext {
  accountId: string; accountEnabled: boolean; ownership: 'personal' | 'pooled';
  /** Older relays omit both fields; decoded legacy responses are retail. */
  protocol?: 'retail' | 'institutional';
  institutional?: { connectionState: 'missing' | 'unverified' | 'verified' | 'revoked' } | null;
  scope: UsScope; scopeUse: 'read_only' | 'execution_bound';
  permissions: { viewOwn: boolean; viewDesk: boolean; buy: boolean; sell: boolean; cancelOwn: boolean; cancelDesk: boolean };
  readiness:
    | { state: 'ready'; canSubmit: boolean; canCancelOwn: boolean; canCancelDesk: boolean }
    | { state: 'unavailable'; reason: string; canSubmit: false; canCancelOwn: false; canCancelDesk: false };
  observedAtMs: number;
}
interface Dependencies {
  session(): UsClientSession | null;
  send(path: string, init: RequestInit, session: UsClientSession): Promise<Response>;
}
const fail = (code = 'US_CLIENT_INVALID_RESPONSE'): never => { throw new UsClientError(code); };
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value)
  ? value as Record<string, unknown> : fail();
const hash = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const same = (a: UsClientSession | null, b: UsClientSession) => a?.userId === b.userId && a.orgId === b.orgId && a.generation === b.generation;
const INSTITUTIONAL_REASON = {
  missing: 'US_INSTITUTIONAL_CONNECTION_MISSING',
  unverified: 'US_INSTITUTIONAL_CONNECTION_UNVERIFIED',
  verified: 'US_INSTITUTIONAL_EXECUTION_PENDING',
  revoked: 'US_INSTITUTIONAL_CONNECTION_REVOKED',
} as const;

/** Read-only account selection. A refresh or selection invalidates previous
 * replies. Call invalidate on unmount; no account, grant or credential writes. */
export class UsAccountClient {
  private generation = 0;
  constructor(private readonly d: Dependencies) {}
  invalidate(): void { this.generation++; }
  private async read(path: string) {
    const s = this.d.session();
    if (!s || !uuid(s.userId) || !uuid(s.orgId) || !Number.isSafeInteger(s.generation) || s.generation < 0) return fail('US_CLIENT_AUTH_REQUIRED');
    const session = { ...s }, generation = ++this.generation;
    const current = () => {
      if (generation !== this.generation || !same(this.d.session(), session)) fail('US_CLIENT_CONTEXT_CHANGED');
    };
    try {
      const response = await this.d.send('/api/polymarket-us/accounts' + path, { method: 'GET', cache: 'no-store',
        redirect: 'error', signal: AbortSignal.timeout(15_000) }, session);
      current();
      const body = record(await response.json()); current();
      if (!response.ok) return fail(typeof body.code === 'string' && /^US_[A-Z0-9_]{1,100}$/.test(body.code)
        ? body.code : 'US_READ_UNAVAILABLE');
      if (body.venue !== 'polymarket_us' || body.source !== 'saved_evidence') return fail();
      return body;
    } catch (error) {
      current();
      if (error instanceof UsClientError) throw error;
      return fail('US_READ_UNAVAILABLE');
    }
  }
  async listAccounts(after?: string): Promise<{ accounts: UsVisibleAccount[]; nextCursor: string | null }> {
    if (after !== undefined && !uuid(after)) return fail('US_CLIENT_INVALID_REQUEST');
    const body = await this.read(after ? '?after=' + after : '');
    if (!Array.isArray(body.accounts) || body.accounts.length > 100
      || !(body.nextCursor === null || uuid(body.nextCursor))) return fail();
    const accounts = body.accounts.map(value => {
      const a = record(value);
      if (!uuid(a.accountId) || typeof a.accountEnabled !== 'boolean' || !['personal','pooled'].includes(String(a.ownership))
        || typeof a.canViewOwn !== 'boolean' || typeof a.canViewDesk !== 'boolean' || (!a.canViewOwn && !a.canViewDesk)
        || (a.ownership === 'personal' && a.canViewDesk) || !['not_evaluated','not_activated'].includes(String(a.execution))) return fail();
      return { accountId: a.accountId, accountEnabled: a.accountEnabled, ownership: a.ownership as UsVisibleAccount['ownership'],
        canViewOwn: a.canViewOwn, canViewDesk: a.canViewDesk, execution: a.execution as UsVisibleAccount['execution'] };
    });
    if (new Set(accounts.map(a => a.accountId)).size !== accounts.length) return fail();
    return { accounts, nextCursor: body.nextCursor };
  }
  async selectAccount(accountId: string, view: 'own' | 'desk'): Promise<UsAccountContext> {
    if (!uuid(accountId) || !['own','desk'].includes(view)) return fail('US_CLIENT_INVALID_REQUEST');
    const a = await this.read('/' + accountId + '/context'), scope = record(a.scope), permissions = record(a.permissions), readiness = record(a.readiness);
    if (a.accountId !== accountId || typeof a.accountEnabled !== 'boolean' || !['personal','pooled'].includes(String(a.ownership))
      || !['read_only','execution_bound'].includes(String(a.scopeUse)) || scope.venue !== 'polymarket_us' || !hash(scope.scopeKey) || !hash(scope.revision)
      || !['viewOwn','viewDesk','buy','sell','cancelOwn','cancelDesk'].every(k => typeof permissions[k] === 'boolean')
      || permissions[view === 'own' ? 'viewOwn' : 'viewDesk'] !== true
      || (a.ownership === 'personal' && (permissions.viewDesk || permissions.cancelDesk))
      || !Number.isSafeInteger(a.observedAtMs) || Number(a.observedAtMs) < 0) return fail();
    let checkedReadiness: UsAccountContext['readiness'];
    if (readiness.state === 'unavailable') {
      if (typeof readiness.reason !== 'string' || !/^US_[A-Z0-9_]{1,100}$/.test(readiness.reason)
        || readiness.canSubmit !== false || readiness.canCancelOwn !== false || readiness.canCancelDesk !== false) return fail();
      checkedReadiness = { state: 'unavailable', reason: readiness.reason, canSubmit: false, canCancelOwn: false, canCancelDesk: false };
    } else if (readiness.state === 'ready') {
      if (!a.accountEnabled || a.scopeUse !== 'execution_bound'
        || !['canSubmit','canCancelOwn','canCancelDesk'].every(k => typeof readiness[k] === 'boolean')
        || (readiness.canSubmit && (!permissions.viewOwn || (!permissions.buy && !permissions.sell)))
        || (readiness.canCancelOwn && (!permissions.viewOwn || !permissions.cancelOwn))
        || (readiness.canCancelDesk && (!permissions.viewDesk || !permissions.cancelDesk || a.ownership !== 'pooled'))) return fail();
      checkedReadiness = { state: 'ready', canSubmit: readiness.canSubmit as boolean,
        canCancelOwn: readiness.canCancelOwn as boolean, canCancelDesk: readiness.canCancelDesk as boolean };
    } else return fail();
    const protocol = a.protocol === undefined ? 'retail' : a.protocol;
    let institutional: UsAccountContext['institutional'] = null;
    if (protocol === 'institutional') {
      const connection = record(a.institutional);
      const state = connection.connectionState;
      if (typeof state !== 'string' || !Object.prototype.hasOwnProperty.call(INSTITUTIONAL_REASON, state)
        || a.scopeUse !== 'read_only' || a.accountEnabled !== false || a.ownership !== 'pooled'
        || checkedReadiness.state !== 'unavailable'
        || checkedReadiness.reason !== INSTITUTIONAL_REASON[state as keyof typeof INSTITUTIONAL_REASON]) return fail();
      institutional = { connectionState: state as keyof typeof INSTITUTIONAL_REASON };
    } else if (protocol !== 'retail' || (a.institutional !== undefined && a.institutional !== null)
      || (a.protocol === 'retail' && a.institutional !== null)
      || (checkedReadiness.state === 'unavailable' && checkedReadiness.reason.startsWith('US_INSTITUTIONAL_'))) return fail();
    return { accountId, accountEnabled: a.accountEnabled, ownership: a.ownership as UsAccountContext['ownership'],
      protocol, institutional,
      scope: { venue: 'polymarket_us', scopeKey: scope.scopeKey, revision: scope.revision }, scopeUse: a.scopeUse as UsAccountContext['scopeUse'],
      permissions: { viewOwn: permissions.viewOwn as boolean, viewDesk: permissions.viewDesk as boolean,
        buy: permissions.buy as boolean, sell: permissions.sell as boolean, cancelOwn: permissions.cancelOwn as boolean, cancelDesk: permissions.cancelDesk as boolean },
      readiness: checkedReadiness,
      observedAtMs: Number(a.observedAtMs) };
  }
}

/** Every account this login can see, in the relay's order (first `maxPages`
 * pages). Surfaces that number accounts for the trader read through this so
 * the same account gets the same number everywhere (see `usAccountLabel`). */
export async function listVisibleUsAccounts(accounts: Pick<UsAccountClient, 'listAccounts'>, maxPages = 3): Promise<UsVisibleAccount[]> {
  const rows: UsVisibleAccount[] = [];
  let after: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const next = await accounts.listAccounts(after);
    rows.push(...next.accounts);
    if (!next.nextCursor) break;
    after = next.nextCursor;
  }
  return rows;
}
